import type { RestrictedCustomer } from 'api/recoveryAi.types'
import { buildTranslate } from 'locales'
import React, { useEffect, useRef, useState } from 'react'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'

const t = buildTranslate('recovery_ai.restricted_customers.actions')

type Props = {
  row: RestrictedCustomer
  canManage: boolean
  onRemove: (row: RestrictedCustomer) => void
}

// Per-row overflow menu. Native <button> elements are used DELIBERATELY here (not
// @thanx/uikit <Button>) — this is a justified exemption, not a convention miss:
//   1. The WAI-ARIA menu-button pattern requires `role="menu"`/`role="menuitem"`,
//      `aria-haspopup`, `aria-expanded`, `aria-controls`, `aria-label`,
//      `aria-disabled`, and `id` on the controls. uikit's Button prop type does
//      NOT expose `role`/`aria-*`/`id`, so it cannot express this pattern.
//   2. Shrinking a uikit Button to a borderless menu item via `overrides.BaseButton`
//      REPLACES uikit's own minimal styling (uikit spreads `...overrides` last),
//      collapsing it to baseui's filled-primary default.
// Mirrors the established, merged row-overflow pattern in
// OperationsCenter/Locations/RowOverflowMenu and the RecoveryAI RewardMultiSelect
// remove-× exemption. The controls remain fully a11y-correct (semantic buttons,
// keyboard nav, aria labelling).
const RowActionsMenu = ({ row, canManage, onRemove }: Props) => {
  const [css] = useStyletron()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)

  const closeMenu = () => {
    setOpen(false)
    // Return focus to the trigger so a keyboard user isn't stranded on a hidden
    // element after Escape / outside-click.
    triggerRef.current?.focus()
  }

  useEffect(() => {
    if (!open) return undefined
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) closeMenu()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenu()
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // Focus the first menuitem on open so a keyboard user lands inside the menu and
  // the arrow-key handler has somewhere to move from.
  useEffect(() => {
    if (!open) return
    const firstItem =
      menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')
    firstItem?.focus()
  }, [open])

  // WAI-ARIA menu keyboard nav: Arrow keys move between menuitems (wrapping at the
  // boundaries), Home/End jump to the first/last. Works at any item count.
  const onMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return
    e.preventDefault()
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []
    )
    if (items.length === 0) return
    const currentIndex = items.indexOf(document.activeElement as HTMLElement)
    let nextIndex = currentIndex
    if (e.key === 'ArrowDown') {
      nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0
    } else if (e.key === 'ArrowUp') {
      nextIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1
    } else if (e.key === 'Home') {
      nextIndex = 0
    } else if (e.key === 'End') {
      nextIndex = items.length - 1
    }
    items[nextIndex]?.focus()
  }

  // Stable ids pair the trigger's aria-controls with the menu container's id, and
  // the disabled View-profile item with its sr-only reason via aria-describedby.
  const menuId = `restricted-row-actions-${row.id}`
  const reasonId = `restricted-view-profile-reason-${row.id}`
  const menuLabel = t('menu_aria', { customer: row.customerName })

  const itemClass = css({
    display: 'block',
    width: '100%',
    padding: '12px 16px',
    textAlign: 'left',
    background: 'none',
    border: 'none',
    fontSize: '14px',
    ':focus-visible': {
      outline: `2px solid ${merchantTheme.colors.belizeHole50}`,
    },
  })

  return (
    <div ref={containerRef} className={css({ position: 'relative' })}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={menuLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen(prev => !prev)}
        className={css({
          background: open ? merchantTheme.colors.grey10 : 'none',
          border: 'none',
          padding: '6px 10px',
          borderRadius: '4px',
          color: merchantTheme.colors.grey70,
          cursor: 'pointer',
          fontSize: '20px',
          lineHeight: 1,
        })}
      >
        <i className="fa fa-ellipsis-h" aria-hidden="true" />
      </button>
      {/* sr-only reason for the disabled View-profile item — keyboard/AT-reachable
          via aria-describedby (not hover-only). */}
      <span id={reasonId} className="sr-only">
        {t('view_profile_disabled_reason')}
      </span>
      {open && (
        <div
          id={menuId}
          ref={menuRef}
          role="menu"
          aria-label={menuLabel}
          onKeyDown={onMenuKeyDown}
          className={css({
            position: 'absolute',
            top: '100%',
            right: '0',
            marginTop: '4px',
            minWidth: '200px',
            backgroundColor: merchantTheme.colors.white,
            border: `1px solid ${merchantTheme.colors.grey30}`,
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            zIndex: 10,
          })}
        >
          {/* View profile is disabled pending DATA-4871 (no profile route yet).
              aria-disabled (not native disabled) keeps it discoverable to AT; the
              reason is wired via aria-describedby + title. It is NEVER a link. */}
          <button
            type="button"
            role="menuitem"
            aria-disabled="true"
            aria-describedby={reasonId}
            title={t('view_profile_disabled_reason')}
            className={`${itemClass} ${css({
              color: merchantTheme.colors.grey50,
              cursor: 'not-allowed',
            })}`}
          >
            {t('view_profile')}
          </button>
          {canManage && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                onRemove(row)
                setOpen(false)
              }}
              className={`${itemClass} ${css({
                color: merchantTheme.colors.grey90,
                cursor: 'pointer',
                ':hover': { backgroundColor: merchantTheme.colors.grey05 },
              })}`}
            >
              {t('remove')}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default RowActionsMenu
